// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Waft — bootstrap and the screen machine. Owns which screen is up, the room's Net
// (created ONCE per session, never left for a rematch), the swat input source
// (camera / tap / keys), the shared camera stream, and the rAF loop that feeds
// per-zone energy → the deterministic sim → the board. Rules live in game.ts, the board
// in render.ts, the race in race.ts.

import '@ben-gy/game-engine/mobile.css';
import './styles/main.css';

import { hardenViewport } from '@ben-gy/game-engine/mobile';
import { createSfx } from '@ben-gy/game-engine/sound';
import { createStore } from '@ben-gy/game-engine/storage';
import { createNet, roomAppId, setTurnConfig, type Net } from '@ben-gy/game-engine/net';
import { getTurnConfig } from '@ben-gy/game-engine/turn';
import { createRounds, type RoundInfo, type Rounds, type RoundPlayer } from '@ben-gy/game-engine/rematch';
import { clearRoomInUrl, createLobby, createRoomEntry, normalizeRoomCode, setRoomInUrl } from '@ben-gy/game-engine/lobby';
import { resolveName } from '@ben-gy/game-engine/identity';

import { escapeHtml, fmtScore } from './dom';
import { MODE_IDS, modeOf, zonesOf, type Mode } from './modes';
import { createGame, drainEvents, progress, step as stepGame, type GameEvent, type GameState } from './game';
import { dailySeed, todayUTC, newSeed, seedToCode } from './seed';
import { Board } from './render';
import { Race, seatOf, createOneShot, type RaceMsg } from './race';
import { ReactorBot } from './bot';
import { runCountdown } from './countdown';
import {
  cameraSupported,
  createCameraSource,
  createManualSource,
  createSyntheticSource,
  requestCamera,
  type SourceKind,
  type SwatSource,
  type SyntheticSource,
} from './input';
import { aboutHtml, ATTRIB, cameraNoticeHtml, helpHtml, menuHtml, multiResultsHtml, soloResultsHtml } from './ui';

const SLUG = 'waft';

// ── shell ─────────────────────────────────────────────────────────────────────
const app = document.querySelector<HTMLElement>('#app')!;
let content: HTMLElement;

const store = createStore(SLUG);
const sfx = createSfx({
  muted: store.get<boolean>('muted', false) ?? false,
  patches: {
    catch: { type: 'triangle', freq: [720, 1200], dur: 0.12, gain: 0.22 },
    miss: { type: 'sine', freq: [430, 240], dur: 0.14, gain: 0.13 },
    sting: { type: 'sawtooth', freq: [240, 70], dur: 0.26, gain: 0.3, noise: true },
  },
});
const reducedMotion = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

let unlocked = false;
function unlock(): void {
  if (unlocked) return;
  unlocked = true;
  try {
    sfx.unlock();
  } catch {
    /* audio unlock is best-effort */
  }
}
window.addEventListener('pointerdown', unlock, { once: true });
window.addEventListener('keydown', unlock, { once: true });

// ── persistent session state ───────────────────────────────────────────────────
let playerName = resolveName(store, () => `Player ${Math.floor(Math.random() * 900 + 100)}`);
void playerName;
let myModeChoice = modeOf(store.get<string>('mode', '')).id;
let hostOptsMode = myModeChoice;

const bestKey = (id: string): string => `best_${id}`;
function bestFor(id: string): number {
  return store.get<number>(bestKey(id), 0) ?? 0;
}
function recordBest(id: string, score: number): boolean {
  const cur = bestFor(id);
  if (score > cur) {
    store.set(bestKey(id), score);
    return true;
  }
  return false;
}

let net: Net | null = null;
let rounds: Rounds | null = null;
let race: Race | null = null;
let roster: RoundPlayer[] = [];
let tally: number[] = [];
let roomCode = '';

// ── input ───────────────────────────────────────────────────────────────────────
let inputMode: SourceKind | null = null;
let inputForced: SourceKind | null = null;
let useSynthetic = false; // test hook only
let synthetic: SyntheticSource | null = null; // the active synthetic source (per grid)
let heldStream: MediaStream | null = null;

const preferManual = (): boolean => store.get<boolean>('preferManual', false) ?? false;

function isTouchDevice(): boolean {
  return (typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches) || 'ontouchstart' in window;
}

function stopCamera(): void {
  if (heldStream) {
    try {
      for (const t of heldStream.getTracks()) t.stop();
    } catch {
      /* best-effort — the point is the camera indicator goes out */
    }
    heldStream = null;
  }
}

/** Show the camera notice (first time) and resolve with a stream, or null for taps. */
function askCamera(): Promise<MediaStream | null> {
  return new Promise((resolve) => {
    if (preferManual()) return resolve(null);
    if (store.get<boolean>('seenCamNote', false)) {
      void requestCamera().then(resolve);
      return;
    }
    const wrap = document.createElement('div');
    wrap.className = 'overlay';
    wrap.innerHTML = `<div class="sheet">${cameraNoticeHtml()}</div>`;
    document.body.appendChild(wrap);
    const close = (): void => wrap.remove();
    wrap.querySelector('#camYes')!.addEventListener('click', () => {
      store.set('seenCamNote', true);
      close();
      void requestCamera().then(resolve); // within this click's activation
    });
    wrap.querySelector('#camNo')!.addEventListener('click', () => {
      store.set('seenCamNote', true);
      store.set('preferManual', true);
      close();
      resolve(null);
    });
  });
}

/** Decide the input scheme — MUST be called from a user gesture (camera permission). */
async function prepareInput(): Promise<void> {
  if (useSynthetic) {
    inputMode = 'synthetic';
    return;
  }
  if (inputForced) {
    inputMode = inputForced;
    return;
  }
  if (!cameraSupported() || preferManual()) {
    inputMode = 'manual';
    return;
  }
  if (!heldStream) {
    const stream = await askCamera();
    if (stream) heldStream = stream;
  }
  inputMode = heldStream ? 'camera' : 'manual';
}

interface GameSource {
  source: SwatSource;
  announce: string;
  perGame: boolean;
}
function makeGameSource(boardHost: HTMLElement, mode: Mode): GameSource {
  const grid = { cols: mode.cols, rows: mode.rows };
  if (inputMode === 'synthetic' || useSynthetic) {
    synthetic = createSyntheticSource(grid);
    return { source: synthetic, announce: 'Test feed', perGame: true };
  }
  if (inputMode === 'camera' && heldStream) {
    const cam = createCameraSource(grid, heldStream, false); // session owns the stream
    return { source: cam, announce: 'Wave your hand to catch the fireflies', perGame: true };
  }
  const src = createManualSource(boardHost, grid);
  const announce = isTouchDevice()
    ? cameraSupported()
      ? 'No camera — tap the fireflies'
      : 'Tap the fireflies'
    : 'Click a firefly, or press its number key';
  return { source: src, announce, perGame: true };
}

// ── screen lifecycle ────────────────────────────────────────────────────────────
const cleanups: Array<() => void> = [];
function onCleanup(fn: () => void): void {
  cleanups.push(fn);
}
function teardown(): void {
  for (const fn of cleanups.splice(0)) {
    try {
      fn();
    } catch {
      /* a failing teardown must never block the next screen */
    }
  }
}
function setPlaying(on: boolean): void {
  document.body.classList.toggle('playing', on);
}

function mountShell(): void {
  app.innerHTML = `<main class="main-content" id="content"></main><footer class="site-footer">${ATTRIB}</footer>`;
  content = document.getElementById('content')!;
}

function sheet(html: string): void {
  const wrap = document.createElement('div');
  wrap.className = 'overlay';
  wrap.innerHTML = `<div class="sheet">${html}</div>`;
  document.body.appendChild(wrap);
  const close = (): void => wrap.remove();
  wrap.addEventListener('pointerdown', (e) => {
    if (e.target === wrap) close();
  });
  wrap.querySelectorAll('.sheet-close').forEach((b) => b.addEventListener('click', close));
}

function toast(msg: string): void {
  const t = document.createElement('div');
  t.className = 'toast';
  t.textContent = msg;
  document.body.appendChild(t);
  setTimeout(() => t.classList.add('show'), 10);
  setTimeout(() => t.remove(), 1900);
}

// ── menu ────────────────────────────────────────────────────────────────────────
function showMenu(): void {
  teardown();
  stopCamera(); // camera off whenever we're at the true menu
  setPlaying(false);
  content.innerHTML = menuHtml({ modeId: myModeChoice, best: bestFor(myModeChoice), muted: sfx.muted(), dateStr: todayUTC() });

  content.querySelectorAll<HTMLElement>('.chip').forEach((c) =>
    c.addEventListener('click', () => {
      myModeChoice = modeOf(c.dataset.mode).id;
      store.set('mode', myModeChoice);
      try {
        sfx.play('select');
      } catch {
        /* best-effort */
      }
      showMenu();
    }),
  );
  content.querySelector('#playDaily')!.addEventListener('click', () => void startSolo(true));
  content.querySelector('#playRandom')!.addEventListener('click', () => void startSolo(false));
  content.querySelector('#playFriends')!.addEventListener('click', () => void beginFriends());
  content.querySelector('#help')!.addEventListener('click', () => sheet(helpHtml()));
  content.querySelector('#about')!.addEventListener('click', () => sheet(aboutHtml()));
  content.querySelector('#mute')!.addEventListener('click', () => {
    sfx.setMuted(!sfx.muted());
    store.set('muted', sfx.muted());
    showMenu();
  });

  if (!store.get<boolean>('seenHelp', false)) {
    store.set('seenHelp', true);
    sheet(helpHtml());
  }
}

// ── the game screen (shared by solo + multiplayer) ────────────────────────────────
interface GameConfig {
  mode: Mode;
  state: () => GameState;
  advance: (energy: readonly number[], dtMs: number) => GameEvent[];
  isOver: () => boolean;
  onOver: () => void;
  onQuit: () => void;
  allowRestart: boolean;
  onRestart?: () => void;
  rivals?: () => Array<{ name: string; score: number; catches: number; finished: boolean }>;
}
interface GameScreen {
  destroy(): void;
}

// The active source of the current game screen, exposed only so the browser-verification
// hook can drive the REAL input→sim path synchronously (rAF is throttled in a hidden
// tab). Never read by gameplay.
let activeSource: SwatSource | null = null;

function juice(events: GameEvent[]): void {
  for (const e of events) {
    try {
      if (e.t === 'catch') sfx.play('catch', { pitch: 1 + Math.min(e.combo, 20) * 0.03 });
      else if (e.t === 'miss') sfx.play('miss');
      else if (e.t === 'sting') sfx.play('sting');
    } catch {
      /* audio best-effort */
    }
    if (!reducedMotion && 'vibrate' in navigator) {
      try {
        if (e.t === 'catch') navigator.vibrate(8);
        else if (e.t === 'sting') navigator.vibrate([25, 35, 25]);
      } catch {
        /* haptics best-effort */
      }
    }
  }
}

function buildGameScreen(cfg: GameConfig): GameScreen {
  const mode = cfg.mode;
  content.innerHTML = `
    <section class="screen game">
      <div class="hud">
        <span class="hud-mode">${escapeHtml(mode.name)}</span>
        <span class="hud-score" id="hudScore">0</span>
        <span class="hud-combo" id="hudCombo"></span>
        <button class="iconbtn" id="pause" title="Pause">❚❚</button>
        <button class="linkbtn hud-quit" id="quit">Quit</button>
      </div>
      <div class="timer-wrap"><span class="timer-bar" id="timerBar"></span></div>
      <div class="rivals" id="rivals" hidden></div>
      <div class="board-host" id="boardHost">
        <canvas id="cv"></canvas>
        <div class="announce" id="announce" hidden></div>
      </div>
    </section>`;

  const boardHost = content.querySelector<HTMLElement>('#boardHost')!;
  const canvas = content.querySelector<HTMLCanvasElement>('#cv')!;
  const hudScore = content.querySelector<HTMLElement>('#hudScore')!;
  const hudCombo = content.querySelector<HTMLElement>('#hudCombo')!;
  const timerBar = content.querySelector<HTMLElement>('#timerBar')!;
  const announce = content.querySelector<HTMLElement>('#announce')!;
  const pauseBtn = content.querySelector<HTMLButtonElement>('#pause')!;
  const rivalsEl = content.querySelector<HTMLElement>('#rivals')!;
  content.querySelector('#quit')!.addEventListener('click', cfg.onQuit);

  const board = new Board(canvas);
  board.setLayout({ cols: mode.cols, rows: mode.rows });
  board.setShowKeys(inputMode === 'manual' && !isTouchDevice());
  const doResize = (): void => board.resize();
  requestAnimationFrame(doResize);
  window.addEventListener('resize', doResize);
  const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(doResize) : null;
  ro?.observe(boardHost);

  const gs = makeGameSource(boardHost, mode);
  activeSource = gs.source;
  board.setSource(gs.source);
  if (gs.announce) {
    announce.textContent = gs.announce;
    announce.hidden = false;
    setTimeout(() => (announce.hidden = true), 2800);
  }

  let live = false;
  let paused = false;
  let overHandled = false;
  let destroyed = false;
  let last = performance.now();
  let raf = 0;
  let hudTick = 0;
  let shownCombo = 0;

  const cd = runCountdown(boardHost, sfx, () => {
    if (destroyed) return;
    live = true;
    last = performance.now();
  });

  function updateHud(state: GameState): void {
    hudScore.textContent = fmtScore(state.score);
    const remain = 1 - progress(state);
    timerBar.style.width = `${Math.max(0, Math.min(1, remain)) * 100}%`;
    timerBar.classList.toggle('low', remain <= 0.25);
    if (state.combo >= 2) {
      hudCombo.textContent = `×${state.combo}`;
      hudCombo.hidden = false;
      if (state.combo !== shownCombo) {
        hudCombo.classList.remove('pop');
        void hudCombo.offsetWidth;
        hudCombo.classList.add('pop');
      }
    } else {
      hudCombo.hidden = true;
    }
    shownCombo = state.combo;
    if (cfg.rivals) {
      const rs = cfg.rivals();
      if (rs.length) {
        rivalsEl.hidden = false;
        rivalsEl.innerHTML = rs
          .map(
            (r) =>
              `<span class="rival${r.finished ? ' done' : ''}">${escapeHtml(r.name)} <b>${fmtScore(r.score)}</b></span>`,
          )
          .join('');
      }
    }
  }

  function loop(now: number): void {
    if (destroyed) return;
    const dt = Math.min(80, now - last);
    last = now;
    const state = cfg.state();
    const energy = gs.source.poll();
    if (live && !paused && !cfg.isOver()) {
      const events = cfg.advance(energy, dt);
      board.onEvents(events, reducedMotion);
      juice(events);
    }
    board.frame(state, { dtMs: dt, reducedMotion, energy });
    if (++hudTick % 2 === 0) updateHud(state);
    if (cfg.isOver() && !overHandled) {
      overHandled = true;
      updateHud(state);
      setTimeout(() => {
        if (!destroyed) cfg.onOver();
      }, 640);
    }
    raf = requestAnimationFrame(loop);
  }
  raf = requestAnimationFrame(loop);
  updateHud(cfg.state());

  function setPaused(p: boolean): void {
    if (destroyed || cfg.isOver()) return;
    paused = p;
    let ov = boardHost.querySelector<HTMLElement>('.pause-ov');
    if (p) {
      if (!ov) {
        ov = document.createElement('div');
        ov.className = 'pause-ov';
        ov.innerHTML = `<div class="pause-card"><h2>Paused</h2>
          <button class="btn primary" id="resume">Resume</button>
          ${cfg.allowRestart ? '<button class="btn" id="restart">Restart</button>' : ''}
          <button class="btn ghost" id="pquit">Quit</button></div>`;
        boardHost.appendChild(ov);
        ov.querySelector('#resume')!.addEventListener('click', () => setPaused(false));
        ov.querySelector('#restart')?.addEventListener('click', () => cfg.onRestart?.());
        ov.querySelector('#pquit')!.addEventListener('click', cfg.onQuit);
      }
    } else {
      ov?.remove();
      last = performance.now();
    }
  }
  pauseBtn.addEventListener('click', () => setPaused(!paused));

  function onKey(e: KeyboardEvent): void {
    if (e.key === 'p' || e.key === 'P') setPaused(!paused);
    else if ((e.key === 'r' || e.key === 'R') && cfg.allowRestart) cfg.onRestart?.();
  }
  window.addEventListener('keydown', onKey);

  return {
    destroy() {
      destroyed = true;
      if (activeSource === gs.source) activeSource = null;
      cancelAnimationFrame(raf);
      cd.cancel();
      window.removeEventListener('resize', doResize);
      window.removeEventListener('keydown', onKey);
      ro?.disconnect();
      if (gs.perGame) gs.source.dispose();
      board.destroy();
    },
  };
}

// ── solo ──────────────────────────────────────────────────────────────────────────
let soloState: GameState | null = null;
let soloDaily = false;
let soloScreen: GameScreen | null = null;

async function startSolo(daily: boolean): Promise<void> {
  await prepareInput();
  teardown();
  setPlaying(true);
  const mode = modeOf(myModeChoice);
  soloDaily = daily;
  const seed = daily ? dailySeed(mode.id, todayUTC()) : newSeed();
  soloState = createGame(mode, seed);

  soloScreen = buildGameScreen({
    mode,
    state: () => soloState!,
    advance: (energy, dt) => {
      stepGame(soloState!, energy, dt);
      return drainEvents(soloState!);
    },
    isOver: () => soloState!.over,
    onOver: () => endSolo(),
    onQuit: () => showMenu(),
    allowRestart: true,
    onRestart: () => void startSolo(soloDaily),
  });
  onCleanup(() => soloScreen?.destroy());
}

function endSolo(): void {
  const st = soloState!;
  const mode = st.mode;
  const isNewBest = recordBest(mode.id, st.score);
  showSoloResults(isNewBest);
}

function showSoloResults(isNewBest: boolean): void {
  teardown();
  setPlaying(false);
  const st = soloState!;
  const mode = st.mode;
  content.innerHTML = `
    ${soloResultsHtml({
      score: st.score,
      catches: st.catches,
      flies: st.flies,
      bestCombo: st.bestCombo,
      mode,
      best: bestFor(mode.id),
      daily: soloDaily,
      isNewBest,
    })}
    <div class="menu-actions">
      <button class="btn primary" id="again">Play again</button>
      <button class="btn" id="share">Share ${soloDaily ? 'score' : 'this swarm'}</button>
      <button class="btn ghost" id="menu">Main menu</button>
      <button class="btn ghost" id="feedback">Report a problem</button>
    </div>`;
  content.querySelector('#again')!.addEventListener('click', () => void startSolo(soloDaily));
  content.querySelector('#menu')!.addEventListener('click', showMenu);
  content.querySelector('#feedback')!.addEventListener('click', (e) =>
    window.feedback?.open({ returnFocusTo: e.currentTarget as HTMLElement }),
  );
  content.querySelector('#share')!.addEventListener('click', () => void shareSolo(st, mode));
}

function shareSolo(st: GameState, mode: Mode): Promise<void> {
  const head = soloDaily ? `Waft ${todayUTC()} · ${mode.name}` : `Waft · ${mode.name}`;
  const line = `caught ${st.catches}/${st.flies} fireflies for ${fmtScore(st.score)}`;
  const link = soloDaily
    ? 'https://waft.benrichardson.dev'
    : `https://waft.benrichardson.dev/?seed=${seedToCode(st.seed)}&m=${mode.id}`;
  return share(`${head} — ${line}\n${link}`);
}

async function share(text: string): Promise<void> {
  try {
    if (navigator.share) {
      await navigator.share({ text });
      return;
    }
  } catch {
    /* cancelled — fall through to copy */
  }
  try {
    await navigator.clipboard.writeText(text);
    toast('Copied to clipboard');
  } catch {
    toast('Could not copy');
  }
}

// ── multiplayer ───────────────────────────────────────────────────────────────────
let netSend: (m: RaceMsg) => void = () => {};
let stOff: () => void = () => {};
let lobby: { repaint(): void; destroy(): void } | null = null;
let lobbyMounted = false;
let gameScreen: GameScreen | null = null;

function repaintLobby(): void {
  if (lobbyMounted) lobby?.repaint();
}

async function beginFriends(): Promise<void> {
  await prepareInput(); // ask for the camera now, while we still have the tap
  showRoomEntry();
}

function showRoomEntry(): void {
  teardown();
  setPlaying(false);
  content.innerHTML = '<section class="screen"><div id="entry"></div></section>';
  const host = content.querySelector<HTMLElement>('#entry')!;

  const deep = new URL(location.href).searchParams.get('room');
  if (deep) {
    const code = normalizeRoomCode(deep);
    clearRoomInUrl();
    if (code.length >= 3) {
      void enterRoom(code, false);
      return;
    }
  }

  const entry = createRoomEntry({
    container: host,
    onSubmit: (code: string, created: boolean) => void enterRoom(normalizeRoomCode(code), created),
    onCancel: showMenu,
    title: 'Play with friends',
    subtitle: 'Everyone catches the same swarm. Start a room, or type a friend’s code.',
  });
  onCleanup(() => entry.destroy());
}

async function enterRoom(code: string, created: boolean): Promise<void> {
  teardown();
  setPlaying(false);
  roomCode = code;
  setRoomInUrl(code);
  tally = [];
  race = null;

  content.innerHTML = `<section class="screen"><div class="lobby-searching"><span class="spinner"></span> Connecting…</div></section>`;

  try {
    setTurnConfig(await getTurnConfig());
  } catch {
    /* fail open — TURN is an upgrade, never a dependency */
  }

  const n = createNet(
    { appId: roomAppId(SLUG), roomId: code, claimHost: created },
    {
      onHostChange: (_id, isSelfHost) => {
        race?.setHost(isSelfHost);
        repaintLobby();
      },
      onPeers: () => repaintLobby(),
      onPeerLeave: (id) => {
        const seat = seatOf(roster, id);
        if (seat !== null) race?.peerLeft(seat);
        repaintLobby();
        refreshMultiResults();
      },
    },
  );
  net = n;

  const r = createRounds<{ mode: string }>({
    net: n,
    playerName,
    minPlayers: 2,
    roundOpts: () => ({ mode: myModeChoice }),
    onRound: (info) => startNetRound(info),
    onChange: (s) => {
      const ho = s.hostOpts as { mode?: string } | null;
      if (ho && typeof ho.mode === 'string') hostOptsMode = modeOf(ho.mode).id;
      repaintLobby();
    },
  });
  rounds = r;

  const sendStatus = n.channel<RaceMsg>('st', (data, from) => {
    if (!race) return;
    const seat = seatOf(roster, from);
    if (seat === null) return;
    race.receive(data, seat);
    refreshMultiResults();
  });
  netSend = (m) => sendStatus(m);
  stOff = () => sendStatus.off();

  showLobby();
}

function showLobby(): void {
  const n = net;
  const r = rounds;
  if (!n || !r) return;
  lobbyMounted = true;
  setPlaying(false);

  content.innerHTML = `<section class="screen"><div id="lobby-host"></div></section>`;
  const isHost = (): boolean => n.isHost() && n.hostSettled();

  lobby = createLobby({
    container: content.querySelector<HTMLElement>('#lobby-host')!,
    net: n,
    rounds: r,
    roomCode,
    minPlayers: 2,
    maxPlayers: 6,
    onCancel: () => void leaveRoom(),
    modeSlot: () => {
      if (isHost()) {
        return `<div class="lobby-modes" id="lm">${MODE_IDS.map(
          (id) =>
            `<button class="chip mode-btn${id === myModeChoice ? ' on' : ''}" data-mode="${id}" aria-pressed="${id === myModeChoice}">${escapeHtml(modeOf(id).name)}</button>`,
        ).join('')}<p class="mode-blurb">${escapeHtml(modeOf(myModeChoice).blurb)}</p></div>`;
      }
      return `<p class="host-pick">${n.hostSettled() ? `The host chose <strong>${escapeHtml(modeOf(hostOptsMode).name)}</strong>` : 'Waiting for the host…'}</p>`;
    },
    onModeMount: () => {
      if (!isHost()) return;
      content.querySelectorAll<HTMLElement>('#lm .mode-btn').forEach((b) =>
        b.addEventListener('click', () => {
          myModeChoice = modeOf(b.dataset.mode).id;
          store.set('mode', myModeChoice);
          lobby?.repaint();
        }),
      );
    },
  });
  onCleanup(() => {
    lobby?.destroy();
    lobby = null;
  });
}

function startNetRound(info: RoundInfo<{ mode: string }>): void {
  teardown();
  lobbyMounted = false;
  roster = info.players;
  if (tally.length !== roster.length) tally = Array(roster.length).fill(0);
  const mode = modeOf(info.opts?.mode);
  const selfSeat = info.seated ? seatOf(roster, net?.selfId ?? '') : null;
  const names = roster.map((p) => p.name);

  const rc = new Race({
    mode,
    seed: info.seed,
    selfSeat,
    names,
    isHost: info.isHost,
    deps: {
      send: (m) => netSend(m),
      onChange: () => refreshMultiResults(),
      onSelfEnd: () => {
        rounds?.finish();
        setTimeout(() => showMultiResults(), 700);
      },
    },
  });
  race = rc;
  setPlaying(true);

  if (selfSeat === null) {
    showMultiResults();
    return;
  }

  gameScreen = buildGameScreen({
    mode,
    state: () => rc.state,
    advance: (energy, dt) => rc.step(energy, dt),
    isOver: () => rc.state.over,
    onOver: () => {
      /* onSelfEnd already scheduled the results */
    },
    onQuit: () => backToLobby(),
    allowRestart: false,
    rivals: () =>
      [...rc.results.values()]
        .filter((p) => p.seat !== rc.selfSeat && !p.left)
        .map((p) => ({ name: p.name, score: p.score, catches: p.catches, finished: p.finished })),
  });
  onCleanup(() => {
    gameScreen?.destroy();
    gameScreen = null;
  });
}

// ── multiplayer results ──────────────────────────────────────────────────────────
let resultsMounted = false;
let winsAwarded = false;
const closeWait = createOneShot(25_000, () => refreshMultiResults());

function showMultiResults(): void {
  teardown();
  setPlaying(false);
  resultsMounted = true;
  winsAwarded = false;
  content.innerHTML = `
    <section class="screen results" id="mres"></section>
    <div class="menu-actions" id="mact">
      <button class="btn primary" id="again">Play again</button>
      <p class="waiting-note" id="waiting" aria-live="polite"></p>
      <button class="btn" id="lobby">Back to lobby</button>
      <button class="btn ghost" id="menu">Main menu</button>
      <button class="btn ghost" id="feedback">Report a problem</button>
    </div>`;
  const again = content.querySelector<HTMLButtonElement>('#again')!;
  again.addEventListener('click', () => {
    rounds?.vote();
    again.disabled = true;
    again.textContent = 'Waiting for the others…';
  });
  content.querySelector('#lobby')!.addEventListener('click', () => backToLobby());
  content.querySelector('#menu')!.addEventListener('click', () => void leaveRoom());
  content.querySelector('#feedback')!.addEventListener('click', (e) =>
    window.feedback?.open({ returnFocusTo: e.currentTarget as HTMLElement }),
  );
  onCleanup(() => {
    resultsMounted = false;
    closeWait.cancel();
  });
  closeWait.poke();
  refreshMultiResults();
}

function refreshMultiResults(): void {
  if (!resultsMounted || !race) return;
  const rc = race;
  const box = content.querySelector<HTMLElement>('#mres');
  if (!box) return;

  if (!winsAwarded && rc.everyoneDone()) {
    winsAwarded = true;
    const top = rc.ranked().filter((r) => !r.left);
    if (top.length && top[0].catches + top[0].score > 0) tally[top[0].seat] = (tally[top[0].seat] ?? 0) + 1;
  }

  box.innerHTML = multiResultsHtml(rc.ranked(), rc.selfSeat, rc.mode, tally);

  const waiting = content.querySelector<HTMLElement>('#waiting');
  if (waiting) {
    const r = rounds?.state();
    if (r && r.startsInMs !== null) {
      waiting.textContent = `Next round in ${Math.ceil(r.startsInMs / 1000)}s — ${r.votes.length}/${Math.max(r.present.length, 1)} ready`;
    } else if (!rc.everyoneDone()) {
      const left = [...rc.results.values()].filter((x) => !x.finished && !x.left).map((x) => x.name);
      waiting.textContent = left.length ? `Still catching: ${left.join(', ')}` : '';
    } else if (r && r.votes.length) {
      waiting.textContent = `${r.votes.length}/${Math.max(r.present.length, 1)} ready for another round`;
    } else {
      waiting.textContent = '';
    }
  }
}

function backToLobby(): void {
  teardown();
  race = null;
  if (net && rounds) showLobby();
  else showMenu();
}

async function leaveRoom(): Promise<void> {
  teardown();
  lobbyMounted = false;
  race = null;
  const n = net;
  const r = rounds;
  net = null;
  rounds = null;
  netSend = () => {};
  try {
    stOff();
  } catch {
    /* channel may already be gone */
  }
  stOff = () => {};
  r?.destroy();
  if (n) {
    try {
      await n.leave();
    } catch {
      /* leaving is best-effort */
    }
  }
  showMenu();
}

// ── boot ────────────────────────────────────────────────────────────────────────
function boot(): void {
  hardenViewport();
  mountShell();
  // An async-seed / daily deep link: ?seed=CODE&m=mode plays that exact swarm solo.
  const params = new URL(location.href).searchParams;
  const room = params.get('room');
  const seedParam = params.get('seed');
  if (room && normalizeRoomCode(room).length >= 3) {
    void beginFriends();
  } else if (seedParam) {
    const m = params.get('m');
    if (m) myModeChoice = modeOf(m).id;
    const seedNum = parseInt(seedParam.trim().toLowerCase(), 36);
    if (Number.isFinite(seedNum) && seedNum > 0) {
      clearRoomInUrl();
      void startSeed(seedNum >>> 0);
      return;
    }
    showMenu();
  } else {
    showMenu();
  }
  window.addEventListener('beforeunload', () => {
    stopCamera();
    void net?.leave();
  });
}

async function startSeed(seed: number): Promise<void> {
  await prepareInput();
  teardown();
  setPlaying(true);
  const mode = modeOf(myModeChoice);
  soloDaily = false;
  soloState = createGame(mode, seed);
  soloScreen = buildGameScreen({
    mode,
    state: () => soloState!,
    advance: (energy, dt) => {
      stepGame(soloState!, energy, dt);
      return drainEvents(soloState!);
    },
    isOver: () => soloState!.over,
    onOver: () => endSolo(),
    onQuit: () => showMenu(),
    allowRestart: true,
    onRestart: () => void startSolo(false),
  });
  onCleanup(() => soloScreen?.destroy());
}

// The browser-verification pass drives this to prove the motion → catch → score wiring
// end-to-end WITHOUT a real camera (synthetic feed), and to read state (test-only).
(window as unknown as { __waft?: unknown }).__waft = {
  useSynthetic(): void {
    useSynthetic = true;
  },
  forceInput(kind: SourceKind): void {
    inputForced = kind;
  },
  pulse(zone: number): void {
    synthetic?.pulse(zone);
  },
  set(zone: number, e: number): void {
    synthetic?.set(zone, e);
  },
  clear(): void {
    synthetic?.clear();
  },
  state(): { score: number; combo: number; catches: number; flies: number; over: boolean; mode: string } | null {
    const s = soloState ?? race?.state;
    if (!s) return null;
    return { score: s.score, combo: s.combo, catches: s.catches, flies: s.flies, over: s.over, mode: s.mode.id };
  },
  zones(): number {
    const s = soloState ?? race?.state;
    return s ? zonesOf(s.mode) : 0;
  },
  // Which zones currently hold an alive mote — so the browser pass can target the REAL
  // input path (a click / key press) at a firefly that is actually there right now.
  alive(): Array<{ zone: number; kind: string }> {
    const s = soloState ?? race?.state;
    if (!s) return [];
    const out: Array<{ zone: number; kind: string }> = [];
    s.byZone.forEach((m, z) => {
      if (m && m.state === 'alive') out.push({ zone: z, kind: m.kind });
    });
    return out;
  },
  // Advance the CURRENT game synchronously (rAF is throttled in a hidden tab). Reads the
  // REAL active source each chunk — so setting synthetic energy and stepping proves the
  // injected-sample → sim wiring; `bot:true` drives a ReactorBot to a full high score,
  // proving the objective is completable through the shipped code path.
  stepFor(ms: number, bot = false): unknown {
    const s = soloState ?? race?.state;
    if (!s) return null;
    const botCtrl = bot ? new ReactorBot({ reactionMs: 90 }) : null;
    const chunk = 1000 / 60;
    for (let t = 0; t < ms && !s.over; t += chunk) {
      const energy = botCtrl ? botCtrl.energy(s) : (activeSource?.poll() ?? []);
      if (soloState) stepGame(soloState, energy, chunk);
      else race?.step(energy, chunk);
    }
    return { score: s.score, combo: s.combo, catches: s.catches, flies: s.flies, over: s.over };
  },
};

boot();
